import { GEOCODE_TIERS, GEOLOCATION_CONFIG, type GeocodeTier } from '@/features/geolocation/config';
import type {
  AddressInput,
  GeocodeResult,
  LocationPrecision,
} from '@/features/geolocation/types/location';

/** Nominatim address types that identify an individual building or premise. */
const BUILDING_ADDRESS_TYPES = [
  'building',
  'house',
  'house_number',
  'amenity',
  'shop',
  'office',
  'industrial',
];

/** Nominatim address types that identify a street rather than a premise. */
const STREET_ADDRESS_TYPES = ['road', 'residential', 'street'];

/** Which address fields each tier sends. Nominatim requires ALL of them to match. */
const TIER_FIELDS: Record<GeocodeTier, (keyof AddressInput)[]> = {
  full: ['street', 'city', 'postalcode', 'country'],
  cityPostal: ['city', 'postalcode', 'country'],
  cityOnly: ['city', 'country'],
};

/**
 * Which fields a tier cannot work without. The `full` tier sends the postcode
 * when it is there but does not need it — an address without a postcode is still
 * worth a street-level lookup.
 */
const TIER_REQUIRED_FIELDS: Record<GeocodeTier, (keyof AddressInput)[]> = {
  full: ['street', 'city', 'country'],
  cityPostal: ['city', 'postalcode', 'country'],
  cityOnly: ['city', 'country'],
};

/** Deterministic order, independent of the tier, for a stable cache key. */
const CACHE_KEY_FIELD_ORDER: (keyof AddressInput)[] = ['country', 'postalcode', 'city', 'street'];

function normalisePart(value: string | null): string {
  // Diacritics are KEPT — Nominatim handles them, and stripping them risks
  // collapsing two genuinely different places onto one cache entry.
  return (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function isBlank(value: string | null): boolean {
  return normalisePart(value) === '';
}

/**
 * Tiers that can actually be sent for this address, in fallback order.
 *
 * Returns `[]` when `city` or `country` is missing — that disables both the
 * debounced auto-locate and the explicit button, which is the main guard keeping
 * request volume near-minimal while a user fills the form field by field.
 */
export function resolveApplicableTiers(address: AddressInput): GeocodeTier[] {
  if (isBlank(address.city) || isBlank(address.country)) return [];

  return GEOCODE_TIERS.filter((tier) =>
    TIER_REQUIRED_FIELDS[tier].every((field) => !isBlank(address[field]))
  );
}

export function nextTier(tiers: GeocodeTier[], current: GeocodeTier): GeocodeTier | null {
  const index = tiers.indexOf(current);
  if (index === -1) return null;
  return tiers[index + 1] ?? null;
}

/** Structured Nominatim params. Never combined with a free-form `q`. */
export function buildTierQuery(tier: GeocodeTier, address: AddressInput): Record<string, string> {
  const query: Record<string, string> = {};

  for (const field of TIER_FIELDS[tier]) {
    const value = address[field]?.trim();
    if (!value) continue;
    query[field] = field === 'country' ? value.toLowerCase() : value;
  }

  return query;
}

/**
 * Normalised, deterministic, tier-scoped cache key.
 *
 * Because the `cityPostal` key omits the street, every supply point sharing a
 * city and postcode shares one cache entry — a tier-2 fallback for a whole
 * neighbourhood costs a single Nominatim request, ever.
 */
export function addressCacheKey(tier: GeocodeTier, address: AddressInput): string {
  const fields = TIER_FIELDS[tier];

  return CACHE_KEY_FIELD_ORDER.filter((field) => fields.includes(field))
    .map((field) => normalisePart(address[field]))
    .filter((part) => part !== '')
    .join('|');
}

/**
 * How exactly the hit pins down the installation.
 *
 * A non-`full` tier is a centroid by construction, so it is capped at `'area'`
 * no matter how precise the returned place looks.
 */
export function classifyPrecision(result: GeocodeResult, tier: GeocodeTier): LocationPrecision {
  if (tier !== 'full') return 'area';

  const { addressType, placeRank } = result;

  if (addressType && BUILDING_ADDRESS_TYPES.includes(addressType)) return 'building';
  if (placeRank !== null && placeRank >= GEOLOCATION_CONFIG.precision.buildingPlaceRank) {
    return 'building';
  }

  if (addressType && STREET_ADDRESS_TYPES.includes(addressType)) return 'street';
  if (placeRank !== null && placeRank >= GEOLOCATION_CONFIG.precision.streetPlaceRank) {
    return 'street';
  }

  // Something coarse but usable (a town, a suburb, a postcode area).
  if (addressType || placeRank !== null) return 'area';

  return 'unknown';
}
